import axios from 'axios'
import {Config} from '../utils/Config'
import {identityAuth} from '../auth/IdentityAuth'

const isEmptyValue = value => {
  if (value == null) return true
  if (Array.isArray(value) || typeof value === 'string') return value.length === 0
  if (typeof value === 'object') return Object.keys(value).length === 0
  return false
}

const UN_AUTHORIZED = 401
const FORBIDDEN = 403

// The legacy token is still written by the super-admin redirect route and read by the other
// HTTP clients, so it stays as a fallback for when there is no Identity session at all.
const getToken = async () => {
  const token =
    (await identityAuth.getFreshToken()) || localStorage.getItem('token')

  return token ? `Bearer ${token}` : null
}

const attachAuthorizationHeader = async config => {
  const token = await getToken()
  const headers = config.headers || {}

  if (token) {
    headers.Authorization = token
  } else {
    delete headers.Authorization
  }

  return {
    ...config,
    headers,
  }
}

// The load balancer in front of the API gives up at 30s, so a request still outstanding after
// that is never going to be answered - the browser is waiting on a connection the LB has already
// abandoned. These used to be 12,000,000ms (3h20m), which meant a stalled request never rejected:
// the promise never settled, so callers' .finally() never ran and anything they had disabled while
// waiting - the Publish button above all - stayed disabled until the page was reloaded.
// File uploads get their own, longer budget because the body itself takes time to go up.
const REQUEST_TIMEOUT_MS = 35000
const UPLOAD_TIMEOUT_MS = 120000

const clientForm = axios.create({
  responseType: 'json',
  baseURL: Config.BASE_URL,
  transformResponse: response => response,
})

clientForm.defaults.headers.post['Content-Type'] = 'multipart/form-data'
clientForm.defaults.headers.post.Accept = 'application/json'
clientForm.defaults.headers.pragma = 'no-cache'
clientForm.defaults.withCredentials = false
clientForm.defaults.timeout = UPLOAD_TIMEOUT_MS

const client = axios.create({
  responseType: 'json',
  baseURL: Config.BASE_URL,
  transformResponse: response => response,
})

client.defaults.headers.post['Content-Type'] = 'application/json'
client.defaults.headers.post.Accept = 'application/json'
client.defaults.headers.pragma = 'no-cache'
client.defaults.withCredentials = false
client.defaults.timeout = REQUEST_TIMEOUT_MS
clientForm.interceptors.request.use(attachAuthorizationHeader)
client.interceptors.request.use(attachAuthorizationHeader)

const tokenSentWith = config =>
  config?.headers?.Authorization?.replace(/^Bearer /, '') ?? null

// A 401 usually means the token went stale between the last session check and this call, so
// renew it once and replay; the request interceptor attaches the renewed token. If Identity
// cannot issue a different token, the original 401 stands.
const sendWithSessionRenewal = async (httpClient, options) => {
  try {
    return await httpClient(options)
  } catch (error) {
    if (error?.response?.status !== UN_AUTHORIZED) throw error
    if (!(await identityAuth.renewRejectedToken(tokenSentWith(error.config))))
      throw error

    return httpClient(options)
  }
}

const request = options => {
  // Special handling for Point API requests
  // if (options.url === 'https://point.widgit.com/point.php') {
  //   const params = new URLSearchParams();
  //   Object.keys(options.data || {}).forEach(key => {
  //     params.append(key, options.data[key]);
  //   });

  //   return axios({
  //     url: options.url,
  //     method: options.method || 'POST',
  //     headers: {
  //       'Content-Type': 'application/x-www-form-urlencoded',
  //       'Accept': 'application/json',
  //       ...(options.headers || {})
  //     },
  //     data: params.toString()
  //   }).then(response => {
  //     if (typeof response.data === 'string') return JSON.parse(response.data)
  //     return response.data
  //   }).catch(error => {
  //     console.warn('Point API request failed:', error);
  //     return Promise.reject(error);
  //   });
  // }

  const onSuccess = response => {
    // A call that returns no content - a 204 from a delete that worked - arrives as an empty
    // string. Parsing that threw "Unexpected end of JSON input", and callers reported the
    // delete as failed although it had happened.
    if (response.status === 204) return null
    if (typeof response.data === 'string') {
      const body = response.data.trim()
      return body.length ? JSON.parse(body) : null
    }
    return response.data
  }

  const onFailure = error => {
    const {response, status, message, headers, data} = error

    // A timeout is not "the server said no" - the request may well have been carried out. Callers
    // need to tell the two apart so they can say so rather than claim a definite failure.
    if (error?.code === 'ECONNABORTED' || error?.code === 'ETIMEDOUT') {
      return Promise.reject({message, success: false, timedOut: true})
    }
    if (
      (response &&
        (response.status === UN_AUTHORIZED || response.status === FORBIDDEN)) ||
      status === UN_AUTHORIZED ||
      status === FORBIDDEN
    ) {
      console.error('status:', status)
      console.error('data:', data)
      console.error('headers:', headers)
      const errorResponse = isEmptyValue(response)
        ? {message, success: false, status: status || UN_AUTHORIZED}
        : {
            message: response?.data || message,
            success: false,
            status: response?.status,
          }
      return Promise.reject(errorResponse)
    }

    console.error('error message', message)
    if (isEmptyValue(response)) {
      var errorResponse = {message, success: false}
      return Promise.reject(errorResponse)
    }

    // Most error bodies are JSON, but a plain-text one (a refused change saying why) used to throw
    // here, so the caller got a parse error in place of the server's reason.
    try {
      return Promise.reject(JSON.parse(response.data) || JSON.parse(message))
    } catch {
      return Promise.reject({message: response.data || message, success: false, status: response.status})
    }
  }
  const httpClient = options.contentType ? clientForm : client

  return sendWithSessionRenewal(httpClient, options)
    .then(onSuccess)
    .catch(onFailure)
}

export default request
